import React, { useEffect, useState } from 'react';
import ThemeToggle from './ThemeToggle';
import DesktopHomeLogo from './DesktopHomeLogo';

interface AppHeaderProps {
  title: React.ReactNode;
  eyebrow?: React.ReactNode;
  meta?: React.ReactNode;
  details?: React.ReactNode;
  backAction?: {
    onClick: () => void;
    ariaLabel?: string;
    title?: string;
  };
  actions?: React.ReactNode;
  menuContent?: React.ReactNode;
  containerClassName?: string;
  useDesktopLogoOffset?: boolean;
  /** Mobile-only: eyebrow, meta and details collapse behind a tap on the title. Desktop layout is unaffected. */
  mobileCollapsible?: boolean;
  mobileExpanded?: boolean;
  onTitleClick?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  title,
  eyebrow,
  meta,
  details,
  backAction,
  actions,
  menuContent,
  containerClassName = 'mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8',
  useDesktopLogoOffset = true,
  mobileCollapsible = false,
  mobileExpanded = false,
  onTitleClick,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = () => {
      if (menuOpen) setMenuOpen(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  return (
    <header className="sticky top-[env(safe-area-inset-top,0px)] z-20 border-b border-white/60 bg-[rgba(247,248,251,0.82)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/80 lg:relative">
      {useDesktopLogoOffset && <DesktopHomeLogo className="absolute left-6 top-4 z-10" />}
      <div className={containerClassName}>
        <div className="flex items-start justify-between gap-4">
          <div className={`flex min-w-0 items-start gap-3 ${useDesktopLogoOffset ? 'lg:pl-40' : ''}`.trim()}>
            {backAction && (
              <button
                type="button"
                onClick={backAction.onClick}
                className="mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-950 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-white/20 dark:hover:text-white"
                aria-label={backAction.ariaLabel || 'Zurück'}
                title={backAction.title || 'Zurück'}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            )}

            <div className="min-w-0">
              {eyebrow && (
                <p className={`text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400 ${mobileCollapsible ? 'hidden sm:block' : ''}`}>{eyebrow}</p>
              )}
              {mobileCollapsible ? (
                <button
                  type="button"
                  onClick={onTitleClick}
                  aria-expanded={mobileExpanded}
                  className="mt-2 flex w-full cursor-pointer items-center gap-2 text-left sm:cursor-default sm:pointer-events-none"
                >
                  <h1 className="min-w-0 flex-1 truncate text-2xl font-semibold text-slate-950 dark:text-slate-100 sm:text-3xl">
                    {title}
                  </h1>
                  <svg
                    className={`h-4 w-4 shrink-0 text-slate-400 transition-transform sm:hidden ${mobileExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              ) : (
                <h1 className="mt-2 truncate text-2xl font-semibold text-slate-950 dark:text-slate-100 sm:text-3xl">
                  {title}
                </h1>
              )}
              {meta && (
                <div className={`mt-3 flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400 ${mobileCollapsible ? (mobileExpanded ? 'flex' : 'hidden sm:flex') : 'flex'}`}>
                  {meta}
                </div>
              )}
              {details && <div className="mt-3 hidden sm:block">{details}</div>}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {actions}
            {menuContent && (
              <div className="relative">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setMenuOpen((current) => !current);
                  }}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-950 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-white/20 dark:hover:text-white"
                  aria-label="Menü öffnen"
                  title="Menü"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <circle cx="10" cy="4" r="1.5" />
                    <circle cx="10" cy="10" r="1.5" />
                    <circle cx="10" cy="16" r="1.5" />
                  </svg>
                </button>

                {menuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_20px_60px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-900 dark:shadow-[0_20px_60px_rgba(2,6,23,0.55)]"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="border-b border-slate-200 px-2 py-2 dark:border-white/10">
                      <ThemeToggle />
                    </div>
                    {menuContent}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {details && (
          <div className={`mt-3 sm:hidden ${mobileCollapsible ? (mobileExpanded ? 'block' : 'hidden') : 'block'}`}>{details}</div>
        )}
      </div>
    </header>
  );
};

export default AppHeader;
